/**
 * video-shopping-reels.js
 * ---------------------------------------------------------------
 * Swiper-powered shopping reels with a fullscreen three-video popup.
 * The popup only ever keeps previous, active, and next videos mounted.
 * ---------------------------------------------------------------
 */
(function () {
  "use strict";

  if (window.__videoReelsInstances === undefined) {
    window.__videoReelsInstances = new Map();
  }

  var PLAYING_VIDEO = null;

  function resetVideo(video) {
    if (!video) return;
    video.pause();
    video.muted = true;
    try {
      video.currentTime = 0;
    } catch (error) {
      /* iOS can reject currentTime before metadata is ready. */
    }
    video.classList.remove("is-playing");
    var media = video.closest(".video-reels__media");
    if (media) media.classList.remove("is-active");
  }

  function unloadVideo(video) {
    if (!video) return;
    if (PLAYING_VIDEO === video) PLAYING_VIDEO = null;
    resetVideo(video);
    video.removeAttribute("src");
    video.load();
  }

  function pauseCurrentlyPlaying(reset) {
    if (!PLAYING_VIDEO) return;
    if (reset) {
      resetVideo(PLAYING_VIDEO);
    } else {
      PLAYING_VIDEO.pause();
      PLAYING_VIDEO.classList.remove("is-playing");
      var media = PLAYING_VIDEO.closest(".video-reels__media");
      if (media) media.classList.remove("is-active");
    }
    PLAYING_VIDEO = null;
  }

  function promoteSrc(video) {
    if (!video) return;
    var src = video.getAttribute("data-src");
    if (src && video.getAttribute("src") !== src) {
      video.preload = "metadata";
      video.setAttribute("src", src);
      video.load();
    }
  }

  function toBool(value, defaultValue) {
    if (value === true || value === false) return value;
    if (value === 1 || value === "1") return true;
    if (value === 0 || value === "0") return false;
    if (typeof value === "string") {
      var normalized = value.toLowerCase().trim();
      if (normalized === "true" || normalized === "on" || normalized === "yes") return true;
      if (normalized === "false" || normalized === "off" || normalized === "no" || normalized === "") return false;
    }
    return defaultValue === true;
  }

  function normalizeSettings(settings) {
    settings = settings || {};
    return {
      sectionId: settings.sectionId,
      autoplay: toBool(settings.autoplay, true),
      loop: toBool(settings.loop, true),
      muted: toBool(settings.muted, true),
      showNavigation: toBool(settings.showNavigation, true),
      showPagination: toBool(settings.showPagination, false),
      cartAddUrl: settings.cartAddUrl || "/cart/add.js",
      cartUrl: settings.cartUrl || "/cart"
    };
  }

  function parseSettings(settingsEl) {
    if (!settingsEl) return {};
    var text = settingsEl.textContent || "{}";

    try {
      return JSON.parse(text);
    } catch (error) {
      return JSON.parse(
        text
          .replace(/:\s*off\s*([,}])/gi, ": false$1")
          .replace(/:\s*on\s*([,}])/gi, ": true$1")
      );
    }
  }

  function isDesktopViewport() {
    return window.matchMedia("(min-width: 750px)").matches;
  }

  function playVideo(video, opts) {
    if (!video) return;
    opts = opts || {};
    if (PLAYING_VIDEO && PLAYING_VIDEO !== video) pauseCurrentlyPlaying(true);

    promoteSrc(video);
    video.muted = opts.muted !== false;
    video.loop = opts.loop !== false;
    video.playsInline = true;

    var startPlayback = function () {
      var playPromise = video.play();
      if (playPromise && typeof playPromise.then === "function") {
        playPromise
          .then(function () {
            video.classList.add("is-playing");
            var media = video.closest(".video-reels__media");
            if (media) media.classList.add("is-active");
            PLAYING_VIDEO = video;
          })
          .catch(function () {
            /* Autoplay can be blocked by browser policy or low power mode. */
          });
      }
    };

    if (opts.immediate) {
      startPlayback();
    } else {
      window.requestAnimationFrame(startPlayback);
    }
  }

  function VideoReels(root) {
    this.root = root;
    this.sectionId = root.getAttribute("data-section-id");
    var settingsEl = root.querySelector("[data-reel-settings]");
    this.settings = normalizeSettings(parseSettings(settingsEl));

    this.viewport = root.querySelector("[data-reel-swiper]");
    this.wrapper = root.querySelector("[data-reel-wrapper]");
    this.popup = root.querySelector("[data-reel-popup]");
    this.popupSwiperEl = root.querySelector("[data-reel-popup-swiper]");
    this.popupWrapper = root.querySelector("[data-reel-popup-wrapper]");
    this.popupCloseBtn = root.querySelector("[data-reel-close]");
    this.popupPrevBtn = root.querySelector("[data-reel-popup-prev]");
    this.popupNextBtn = root.querySelector("[data-reel-popup-next]");

    this.desktopSwiper = null;
    this.popupSwiper = null;
    this.io = null;
    this.reelCards = [];
    this.currentPopupIndex = 0;
    this.lastFocusedEl = null;
    this.savedScrollY = 0;
    this.sectionCanLoad = false;
    this.isPopupOpen = false;
    this.closeTimer = null;

    this._onOpenClick = this._onOpenClick.bind(this);
    this._onAtcClick = this._onAtcClick.bind(this);
    this._onPopupKeydown = this._onPopupKeydown.bind(this);
    this._onPopupClose = this._onPopupClose.bind(this);
    this._onPopupPrev = this._onPopupPrev.bind(this);
    this._onPopupNext = this._onPopupNext.bind(this);

    this.init();
  }

  VideoReels.prototype.init = function () {
    this._initDesktopSwiper();
    this._initSectionObserver();
    this._bindDelegatedEvents();
  };

  VideoReels.prototype._initDesktopSwiper = function () {
    if (!this.viewport || typeof Swiper === "undefined") return;
    var self = this;

    this.desktopSwiper = new Swiper(this.viewport, {
      /* Mobile default (<750px): centered active slide with previous/next
         peeking on both sides. */
      slidesPerView: "auto",
      centeredSlides: true,
      /* When there are too few slides to fill the viewport (e.g. only 1-2
         cards), Swiper would otherwise pack them against the start edge
         instead of centering them. This keeps them centered regardless of
         count, while slides that DO overflow the viewport still behave
         normally and start from the first slide as before. */
      centerInsufficientSlides: true,
      spaceBetween: 18,
      loop: this.settings.loop === true,
      watchOverflow: false,
      watchSlidesProgress: true,
      speed: 450,
      /* Re-measure slide widths if content inside a slide changes size
         after Swiper's initial measurement (e.g. a poster image finishing
         its lazy load) — without this, a stale first measurement can lock
         the active slide in at the wrong width and the mobile peek effect
         never shows. */
      observer: true,
      observeParents: true,
      observeSlideChildren: true,
      /* Desktop (>=750px): identical to the previous, unmodified config. */
      breakpoints: {
        750: {
          slidesPerView: "auto",
          centeredSlides: false,
          spaceBetween: 27
        }
      },
      navigation: false
        ? {
            nextEl: this.root.querySelector("[data-reel-next]"),
            prevEl: this.root.querySelector("[data-reel-prev]")
          }
        : false,
      pagination: this.settings.showPagination
        ? {
            el: this.root.querySelector("[data-reel-pagination]"),
            clickable: true
          }
        : false,
      a11y: { enabled: true },
      on: {
        slideChange: function () {
          self._syncActiveGridVideo();
        },
        resize: function () {
          if (self.desktopSwiper) self.desktopSwiper.update();
          self._syncActiveGridVideo();
        },
        init: function () {
          self._syncActiveGridVideo();
        }
      }
    });

    // Defensive re-measure: fonts/poster images finishing their load can
    // change a slide's rendered width after Swiper's first pass. Two more
    // passes (next frame, and on full window load) catch that without
    // relying solely on the `observer` option above.
    window.requestAnimationFrame(function () {
      if (self.desktopSwiper) self.desktopSwiper.update();
    });
    window.addEventListener(
      "load",
      function () {
        if (self.desktopSwiper) self.desktopSwiper.update();
      },
      { once: true }
    );
  };

  VideoReels.prototype._syncActiveGridVideo = function () {
    if (!this.sectionCanLoad || this.isPopupOpen || !this.wrapper) return;

    var slides = this.wrapper.querySelectorAll(".video-reels__slide");
    var activeSlide = this.wrapper.querySelector(".swiper-slide-active") || slides[0];

    slides.forEach(function (slide) {
      var video = slide.querySelector("[data-reel-video]");
      playVideo(video, { muted: true, loop: true });
      if (video && slide !== activeSlide) resetVideo(video);
    });

    var video = activeSlide ? activeSlide.querySelector("[data-reel-video]") : null;
    if (video) {
      playVideo(video, { muted: true, loop: this.settings.loop });
    } else {
      pauseCurrentlyPlaying(true);
    }
  };

  VideoReels.prototype._initSectionObserver = function () {
    var self = this;
    if (!("IntersectionObserver" in window)) {
      this.sectionCanLoad = true;
      this._syncActiveGridVideo();
      return;
    }

    this.io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          self.sectionCanLoad = true;
          window.requestAnimationFrame(function () {
            self._syncActiveGridVideo();
          });
          self.io.unobserve(self.root);
        });
      },
      { root: null, rootMargin: "120px", threshold: 0.05 }
    );

    this.io.observe(this.root);
  };

  VideoReels.prototype._bindDelegatedEvents = function () {
    this.root.addEventListener("click", this._onOpenClick);
    this.root.addEventListener("click", this._onAtcClick);
  };

  VideoReels.prototype._onOpenClick = function (event) {
    var trigger = event.target.closest("[data-reel-open]");
    if (!trigger || !this.root.contains(trigger)) return;
    var index = parseInt(trigger.getAttribute("data-reel-index"), 10) || 0;
    this.openPopup(index);
  };

  VideoReels.prototype._collectCards = function () {
    var cardsByIndex = [];

    this.wrapper.querySelectorAll(".video-reels__slide").forEach(function (slide) {
      var index = parseInt(slide.getAttribute("data-reel-index"), 10);
      var card = slide.querySelector(".video-reels__card");
      if (Number.isNaN(index) || !card || cardsByIndex[index]) return;
      cardsByIndex[index] = card;
    });

    this.reelCards = cardsByIndex.filter(function (card) {
      return !!card;
    });
  };

  VideoReels.prototype._wrapIndex = function (index) {
    var total = this.reelCards.length;
    if (!total) return 0;
    return ((index % total) + total) % total;
  };

  VideoReels.prototype._clearPopupSlides = function () {
    if (!this.popupWrapper) return;
    this.popupWrapper.querySelectorAll("[data-reel-video]").forEach(unloadVideo);
    this.popupWrapper.textContent = "";
  };

  VideoReels.prototype._unloadGridVideos = function () {
    if (!this.wrapper) return;
    this.wrapper.querySelectorAll("[data-reel-video]").forEach(unloadVideo);
  };

  VideoReels.prototype._createPopupSlide = function (sourceIndex, role) {
    var slide = document.createElement("div");
    slide.className = "swiper-slide video-reels__popup-slide";
    slide.setAttribute("data-popup-role", role);
    slide.setAttribute("data-source-index", sourceIndex);

    var sourceCard = this.reelCards[sourceIndex];
    if (!sourceCard) return slide;

    var clone = sourceCard.cloneNode(true);
    var media = clone.querySelector("[data-reel-open]");
    if (media) {
      media.removeAttribute("data-reel-open");
      media.removeAttribute("data-reel-index");
      media.setAttribute("tabindex", "-1");
    }

    var video = clone.querySelector("[data-reel-video]");
    if (video) {
      video.removeAttribute("src");
      video.preload = "metadata";
      video.playsInline = true;
      promoteSrc(video);
      if (role !== "active") resetVideo(video);
    }

    slide.appendChild(clone);
    return slide;
  };

  VideoReels.prototype._renderPopupTriplet = function () {
    var total = this.reelCards.length;
    var frag = document.createDocumentFragment();

    this._clearPopupSlides();
    if (!total) return;

    frag.appendChild(this._createPopupSlide(this._wrapIndex(this.currentPopupIndex - 1), "prev"));
    frag.appendChild(this._createPopupSlide(this._wrapIndex(this.currentPopupIndex), "active"));
    frag.appendChild(this._createPopupSlide(this._wrapIndex(this.currentPopupIndex + 1), "next"));
    this.popupWrapper.appendChild(frag);

    if (this.popupSwiper) {
      this.popupSwiper.update();
      this.popupSwiper.slideTo(1, 0, false);
    }
  };

  VideoReels.prototype.openPopup = function (startIndex) {
    this._collectCards();
    if (!this.reelCards.length || !this.popupSwiperEl || typeof Swiper === "undefined") return;

    window.clearTimeout(this.closeTimer);
    this.lastFocusedEl = document.activeElement;
    this.savedScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    this.currentPopupIndex = this._wrapIndex(startIndex);
    this.sectionCanLoad = true;
    this.isPopupOpen = true;
    pauseCurrentlyPlaying(true);
    this._unloadGridVideos();
    this._renderPopupTriplet();

    var self = this;
    var isDesktop = isDesktopViewport();
    this.popupSwiper = new Swiper(this.popupSwiperEl, {
      direction: isDesktop ? "horizontal" : "vertical",
      slidesPerView: isDesktop ? 3 : 1,
      centeredSlides: true,
      initialSlide: 1,
      spaceBetween: isDesktop ? 16 : 0,
      speed: 350,
      threshold: 8,
      resistanceRatio: 0.65,
      watchSlidesProgress: true,
      a11y: { enabled: true },
      on: {
        transitionEnd: function () {
          self._onPopupTransitionEnd();
        },
        touchEnd: function () {
          self._syncPopupVideo();
        }
      }
    });

    this.popupSwiper.slideTo(1, 0, false);

    document.documentElement.classList.add("video-reels-lock");
    document.body.classList.add("video-reels-lock");
    this.popup.classList.add("is-open");
    this.popup.setAttribute("aria-hidden", "false");

    document.addEventListener("keydown", this._onPopupKeydown);
    this.popupCloseBtn.addEventListener("click", this._onPopupClose);
    if (this.popupPrevBtn) this.popupPrevBtn.addEventListener("click", this._onPopupPrev);
    if (this.popupNextBtn) this.popupNextBtn.addEventListener("click", this._onPopupNext);

    try {
      this.popupCloseBtn.focus({ preventScroll: true });
    } catch (error) {
      this.popupCloseBtn.focus();
    }
    window.requestAnimationFrame(function () {
      self._syncPopupVideo();
    });
  };

  VideoReels.prototype._onPopupTransitionEnd = function () {
    if (!this.popupSwiper || !this.isPopupOpen) return;

    if (this.popupSwiper.activeIndex === 0) {
      this.currentPopupIndex = this._wrapIndex(this.currentPopupIndex - 1);
      this._renderPopupTriplet();
    } else if (this.popupSwiper.activeIndex === 2) {
      this.currentPopupIndex = this._wrapIndex(this.currentPopupIndex + 1);
      this._renderPopupTriplet();
    }

    var self = this;
    window.requestAnimationFrame(function () {
      self._syncPopupVideo();
    });
  };

  VideoReels.prototype._syncPopupVideo = function () {
    if (!this.popupSwiperEl || !this.isPopupOpen) return;
    var activeSlide = this.popupSwiperEl.querySelector('[data-popup-role="active"]');
    var activeVideo = activeSlide ? activeSlide.querySelector("[data-reel-video]") : null;

    this.popupSwiperEl.querySelectorAll("[data-reel-video]").forEach(function (video) {
      if (video !== activeVideo) resetVideo(video);
    });

    if (activeVideo) {
      playVideo(activeVideo, { muted: false, loop: this.settings.loop, immediate: true });
    } else {
      pauseCurrentlyPlaying(true);
    }
  };

  VideoReels.prototype._onPopupClose = function () {
    this.closePopup();
  };

  VideoReels.prototype._onPopupPrev = function () {
    if (this.popupSwiper) this.popupSwiper.slideTo(0);
  };

  VideoReels.prototype._onPopupNext = function () {
    if (this.popupSwiper) this.popupSwiper.slideTo(2);
  };

  VideoReels.prototype._onPopupKeydown = function (event) {
    if (event.key === "Escape") {
      this.closePopup();
      return;
    }

    if (event.key === "ArrowLeft") {
      event.preventDefault();
      this._onPopupPrev();
      return;
    }

    if (event.key === "ArrowRight") {
      event.preventDefault();
      this._onPopupNext();
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      this._onPopupPrev();
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      this._onPopupNext();
      return;
    }

    if (event.key !== "Tab") return;

    var focusable = this.popup.querySelectorAll(
      'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  VideoReels.prototype.closePopup = function () {
    if (!this.isPopupOpen) return;
    var self = this;

    this.isPopupOpen = false;
    pauseCurrentlyPlaying(true);
    this.popup.classList.remove("is-open");
    this.popup.setAttribute("aria-hidden", "true");

    document.documentElement.classList.remove("video-reels-lock");
    document.body.classList.remove("video-reels-lock");
    document.removeEventListener("keydown", this._onPopupKeydown);
    this.popupCloseBtn.removeEventListener("click", this._onPopupClose);
    if (this.popupPrevBtn) this.popupPrevBtn.removeEventListener("click", this._onPopupPrev);
    if (this.popupNextBtn) this.popupNextBtn.removeEventListener("click", this._onPopupNext);

    if (this.popupSwiper) {
      this.popupSwiper.destroy(true, true);
      this.popupSwiper = null;
    }

    window.clearTimeout(this.closeTimer);
    this.closeTimer = window.setTimeout(function () {
      if (!self.isPopupOpen) self._clearPopupSlides();
    }, 260);

    if (this.lastFocusedEl && typeof this.lastFocusedEl.focus === "function") {
      try {
        this.lastFocusedEl.focus({ preventScroll: true });
      } catch (error) {
        this.lastFocusedEl.focus();
      }
    }

    window.scrollTo(0, this.savedScrollY);

    window.requestAnimationFrame(function () {
      window.scrollTo(0, self.savedScrollY);
      self._syncActiveGridVideo();
    });
  };

  VideoReels.prototype._onAtcClick = function (event) {
    var btn = event.target.closest("[data-reel-atc]");
    if (!btn || !this.root.contains(btn) || btn.disabled || btn.classList.contains("is-loading")) return;

    var variantId = btn.getAttribute("data-variant-id");
    if (!variantId) return;

    var addUrl = this.settings.cartAddUrl || "/cart/add.js";

    btn.classList.remove("is-error");
    btn.classList.add("is-loading");

    fetch(addUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    })
      .then(function (response) {
        if (!response.ok) {
          return response.json().then(function (data) {
            throw new Error((data && data.description) || "Add to cart failed");
          });
        }
        return response.json();
      })
      .then(function () {
        btn.classList.remove("is-loading");
        var label = btn.querySelector(".video-reels__atc-text");
        var originalText = label.textContent;
        label.textContent = "Added";

        document.dispatchEvent(
          new CustomEvent("cart:updated", { bubbles: true, detail: { source: "video-reels" } })
        );
        document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));

        window.setTimeout(function () {
          label.textContent = originalText;
        }, 1800);
      })
      .catch(function () {
        btn.classList.remove("is-loading");
        btn.classList.add("is-error");
        var label = btn.querySelector(".video-reels__atc-text");
        var originalText = label.textContent;
        label.textContent = "Error";
        window.setTimeout(function () {
          btn.classList.remove("is-error");
          label.textContent = originalText;
        }, 1800);
      });
  };

  VideoReels.prototype.destroy = function () {
    pauseCurrentlyPlaying(true);
    window.clearTimeout(this.closeTimer);

    if (this.io) this.io.disconnect();
    if (this.desktopSwiper) this.desktopSwiper.destroy(true, true);
    if (this.popupSwiper) this.popupSwiper.destroy(true, true);
    this._clearPopupSlides();

    this.root.removeEventListener("click", this._onOpenClick);
    this.root.removeEventListener("click", this._onAtcClick);
    document.removeEventListener("keydown", this._onPopupKeydown);
    if (this.popupCloseBtn) this.popupCloseBtn.removeEventListener("click", this._onPopupClose);
    if (this.popupPrevBtn) this.popupPrevBtn.removeEventListener("click", this._onPopupPrev);
    if (this.popupNextBtn) this.popupNextBtn.removeEventListener("click", this._onPopupNext);

    document.documentElement.classList.remove("video-reels-lock");
    document.body.classList.remove("video-reels-lock");
  };

  function mountAll(scope) {
    (scope || document).querySelectorAll("[data-video-reels]").forEach(function (root) {
      if (window.__videoReelsInstances.has(root)) return;
      window.__videoReelsInstances.set(root, new VideoReels(root));
    });
  }

  function unmount(root) {
    var instance = window.__videoReelsInstances.get(root);
    if (instance) {
      instance.destroy();
      window.__videoReelsInstances.delete(root);
    }
  }

  document.addEventListener("DOMContentLoaded", function () {
    mountAll(document);
  });

  document.addEventListener("shopify:section:load", function (event) {
    var root = event.target.querySelector("[data-video-reels]");
    if (root) mountAll(event.target);
  });

  document.addEventListener("shopify:section:unload", function (event) {
    var root = event.target.querySelector("[data-video-reels]");
    if (root) unmount(root);
  });

  document.addEventListener("shopify:block:select", function (event) {
    var slide = event.target.closest(".video-reels__slide");
    if (!slide) return;
    var root = event.target.closest("[data-video-reels]");
    if (!root) return;
    var instance = window.__videoReelsInstances.get(root);
    if (!instance || !instance.desktopSwiper) return;
    var index = Array.prototype.indexOf.call(slide.parentElement.children, slide);
    instance.desktopSwiper.slideTo(index);
  });

  if (document.readyState !== "loading") {
    mountAll(document);
  }
})();